import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTimer } from '../contexts/TimerContext';
import { useProjects } from '../contexts/ProjectContext';
import { Button } from './ui/button';
import { TaskSelector } from './tasks/TaskSelector';
import { ProjectForm } from './projects/ProjectForm';
import { ProjectList } from './projects/ProjectList';
import { TaskList } from './tasks/TaskList';
import { useSettings } from '../contexts/SettingsContext';
import { cn } from '../utils/cn';
import {
  PlayIcon,
  PauseIcon,
  PlusIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  FolderIcon,
  ListBulletIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { TaskForm } from './tasks/TaskForm';
import { showToast } from '@/utils/toast';
import { PomodoroTimer } from '@/components/pomodoro/PomodoroTimer';
import { Pomodoro } from '@/types/pomodoro';
import { useGoals } from '@/contexts/GoalContext';
import { soundManager } from '../utils/sounds';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { UpgradePrompt } from './pricing/UpgradePrompt';
import { MusicPlayer } from './music/MusicPlayer';
import { SessionRecoveryBanner } from '@/components/timer/SessionRecoveryBanner';

export const Timer: React.FC = () => {
  const timer = useTimer();
  const { tasks, addTask, projects, updateTask, updatePomodoro, addPomodoro } = useProjects();

  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [isProjectListOpen, setIsProjectListOpen] = useState(false);
  const [isTaskListOpen, setIsTaskListOpen] = useState(false);
  const [currentPomodoro, setCurrentPomodoro] = useState<Pomodoro | null>(null);
  const [isPomodoroSaved, setIsPomodoroSaved] = useState(false);
  const [recoveredSession, setRecoveredSession] = useState<{
    pomodoroId: string;
    elapsedMinutes: number;
    startTime: number;
  } | null>(null);
  const { updateGoalProgress } = useGoals();
  const { settings, updateSettings } = useSettings();
  const { isPlusOrHigher } = useSubscription();

  // Initialize selected task from settings
  useEffect(() => {
    if (settings.pomodoro.lastSelectedTaskId && tasks.length > 0) {
      const lastTask = tasks.find(task => task.id === settings.pomodoro.lastSelectedTaskId);
      if (lastTask) setSelectedTask(lastTask);
    }
  }, [settings.pomodoro.lastSelectedTaskId, tasks]);

  const handleTaskSelection = (task: any) => {
    setSelectedTask(task);
    if (task) {
      updateSettings({ pomodoro: { lastSelectedTaskId: task.id } });
    }
  };

  const formatTime = (timeInSeconds: number): { minutes: string; seconds: string } => {
    const m = Math.floor(timeInSeconds / 60);
    const s = Math.floor(timeInSeconds % 60);
    return { minutes: m.toString().padStart(2, '0'), seconds: s.toString().padStart(2, '0') };
  };

  const handleCreateTask = async (taskData: any) => {
    try {
      const task = await addTask({
        ...taskData,
        completed: false,
        completedPomodoros: 0,
        projectId: taskData.projectId || null,
        createdAt: Date.now(),
      });
      setSelectedTask(task);
      updateSettings({ pomodoro: { lastSelectedTaskId: task.id } });
      setShowNewTask(false);
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const phaseConfig = {
    work: { label: 'Focus', color: 'blue', gradient: 'from-blue-500/8 via-blue-400/3 to-transparent' },
    shortBreak: { label: 'Short Break', color: 'emerald', gradient: 'from-emerald-500/8 via-emerald-400/3 to-transparent' },
    longBreak: { label: 'Long Break', color: 'violet', gradient: 'from-violet-500/8 via-violet-400/3 to-transparent' },
  };

  const currentPhase = phaseConfig[timer.state.phase];

  const getButtonClasses = () => {
    const colors: Record<string, string> = {
      work: 'bg-blue-500 hover:bg-blue-600 shadow-blue-500/25',
      shortBreak: 'bg-emerald-500 hover:bg-emerald-600 shadow-emerald-500/25',
      longBreak: 'bg-violet-500 hover:bg-violet-600 shadow-violet-500/25',
    };
    return colors[timer.state.phase];
  };

  const getTimerColor = () => {
    const colors: Record<string, string> = {
      work: 'text-blue-500 dark:text-blue-400',
      shortBreak: 'text-emerald-500 dark:text-emerald-400',
      longBreak: 'text-violet-500 dark:text-violet-400',
    };
    return colors[timer.state.phase];
  };

  const handlePomodoroComplete = async (taskId: string) => {
    if (!taskId || taskId === 'no-task') return;
    try {
      const currentTask = tasks.find(t => t.id === taskId);
      if (!currentTask) return;
      await updateTask(taskId, {
        completedPomodoros: Math.min((currentTask.completedPomodoros || 0) + 1, currentTask.estimatedPomodoros)
      });
    } catch (error) {
      console.error('Error updating pomodoro progress:', error);
    }
  };

  // Restore current pomodoro from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('pomomax_current_pomodoro');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.startTime) {
          setCurrentPomodoro(parsed);
          setIsPomodoroSaved(false);

          // Check if this is a stale session that needs recovery (started > 30s ago)
          const startMs = typeof parsed.startTime === 'string'
            ? new Date(parsed.startTime).getTime()
            : parsed.startTime;
          const ageMs = Date.now() - startMs;
          if (ageMs > 30_000) {
            const elapsedMinutes = Math.round(ageMs / 60000);
            setRecoveredSession({
              pomodoroId: parsed.id,
              elapsedMinutes,
              startTime: startMs,
            });
          }
        }
      }
    } catch {}
  }, []);

  // Persist current pomodoro to localStorage whenever it changes
  useEffect(() => {
    if (currentPomodoro) {
      localStorage.setItem('pomomax_current_pomodoro', JSON.stringify(currentPomodoro));
    } else {
      localStorage.removeItem('pomomax_current_pomodoro');
    }
  }, [currentPomodoro]);

  const createPomodoro = useCallback(() => {
    const newPomodoro: Pomodoro = {
      id: typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      startTime: Date.now(),
      duration: timer.state.phase === 'work'
        ? settings.timer.pomodoro * 60
        : timer.state.phase === 'shortBreak'
        ? settings.timer.shortBreak * 60
        : settings.timer.longBreak * 60,
      completed: false,
      interruptions: 0,
      taskId: selectedTask?.id,
      projectId: selectedTask?.projectId,
    };

    setCurrentPomodoro(newPomodoro);
    setIsPomodoroSaved(false);

    const now = Date.now();
    const pomodoroRecord = {
      id: newPomodoro.id,
      taskId: newPomodoro.taskId,
      projectId: newPomodoro.projectId,
      startTime: newPomodoro.startTime,
      endTime: now,
      duration: 1, // placeholder in minutes; updated when session ends
      type: 'work' as const,
      title: selectedTask?.title || 'Pomodoro Session',
      notes: newPomodoro.notes,
      completed: false,
    };

    addPomodoro(pomodoroRecord).then((insertedPomodoro) => {
      setCurrentPomodoro({
        id: insertedPomodoro.id,
        startTime: insertedPomodoro.startTime,
        duration: insertedPomodoro.duration,
        completed: insertedPomodoro.completed,
        interruptions: 0,
        taskId: insertedPomodoro.taskId,
        projectId: insertedPomodoro.projectId,
        notes: insertedPomodoro.notes,
      });
    }).catch((error) => {
      console.error('Error saving pomodoro:', error);
    });
  }, [timer.state.phase, settings.timer, selectedTask, addPomodoro]);

  const savePomodoro = useCallback((actualDuration?: number) => {
    if (currentPomodoro) {
      const endTime = Date.now();
      const startMs = typeof currentPomodoro.startTime === 'string'
        ? new Date(currentPomodoro.startTime).getTime()
        : currentPomodoro.startTime;
      // Store duration in minutes to match DB schema (duration INTEGER -- in minutes)
      let duration = actualDuration || Math.max(Math.round((endTime - (startMs ?? 0)) / 60000), 1);

      updatePomodoro(currentPomodoro.id, { endTime, duration, completed: true }, { silent: true })
        .then(() => { setCurrentPomodoro(null); setIsPomodoroSaved(true); })
        .catch(() => { setCurrentPomodoro(null); setIsPomodoroSaved(true); });

      if (selectedTask) handlePomodoroComplete(selectedTask.id);
      if (settings.goals.autoUpdateGoals) {
        updateGoalProgress(Math.max(duration, 1));
      }
    }
  }, [currentPomodoro, selectedTask, settings, addPomodoro, handlePomodoroComplete, updateGoalProgress]);

  useEffect(() => {
    if (!timer.state.isActive && timer.state.timeRemaining === 0) {
      if (timer.state.phase === 'work') {
        soundManager.stopTicking();
        soundManager.playAlarm();
        savePomodoro();

        if (settings.notifications.desktop && 'Notification' in window && Notification.permission === 'granted') {
          new Notification('Focus session complete!', {
            body: 'Great work! Time for a break.',
            icon: '/favicon.ico',
          });
        }

        // Auto-start break
        if (settings.timer.autoStartBreaks) {
          timer.skip();
          timer.start();
        }
      } else {
        // Break finished
        if (settings.notifications.desktop && 'Notification' in window && Notification.permission === 'granted') {
          new Notification('Break is over!', {
            body: 'Ready to focus again?',
            icon: '/favicon.ico',
          });
        }

        // Auto-start next pomodoro
        if (settings.timer.autoStartPomodoros) {
          timer.skip();
          setTimeout(() => {
            createPomodoro();
            timer.start();
            if (settings.timer.tickingSound !== 'none') soundManager.startTicking();
          }, 100);
        }
      }
    }
  }, [timer.state.phase, timer.state.isActive, timer.state.timeRemaining, savePomodoro, settings.notifications.desktop, settings.timer.autoStartBreaks, settings.timer.autoStartPomodoros]);

  const prevPhaseRef = useRef(timer.state.phase);
  useEffect(() => {
    const prevPhase = prevPhaseRef.current;
    if (prevPhase !== timer.state.phase) {
      if (prevPhase === 'work' && currentPomodoro) savePomodoro();
    }
    prevPhaseRef.current = timer.state.phase;
  }, [timer.state.phase, currentPomodoro]);

  useEffect(() => {
    timer.updateDurations({
      work: settings.timer.pomodoro,
      shortBreak: settings.timer.shortBreak,
      longBreak: settings.timer.longBreak,
    });
  }, [settings.timer]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (currentPomodoro && timer.state.isActive && !isPomodoroSaved) {
        const endTime = Date.now();
        const startMs = typeof currentPomodoro.startTime === 'string'
          ? new Date(currentPomodoro.startTime).getTime()
          : currentPomodoro.startTime;
        const actualTimeElapsed = endTime - startMs;
        // Duration stored in MINUTES in the DB schema
        const duration = Math.max(Math.round(actualTimeElapsed / 60000), 1);
        updatePomodoro(currentPomodoro.id, {
          endTime,
          duration,
          completed: false,
          completedAt: new Date().toISOString(),
        }, { silent: true }).catch(() => {});
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentPomodoro, timer.state.isActive, isPomodoroSaved, updatePomodoro]);

  const handleStart = () => {
    createPomodoro();
    timer.start();
    if (settings.timer.tickingSound !== 'none') soundManager.startTicking();
  };

  const handlePause = () => {
    timer.pause();
    soundManager.stopTicking();
    if (currentPomodoro) {
      setCurrentPomodoro({ ...currentPomodoro, interruptions: currentPomodoro.interruptions + 1 });
    }
  };

  const handleResumeRecovery = () => {
    timer.start();
    setRecoveredSession(null);
  };

  const handleDiscardRecovery = () => {
    if (!recoveredSession) return;
    const duration = Math.max(Math.round((Date.now() - recoveredSession.startTime) / 60000), 1);
    updatePomodoro(recoveredSession.pomodoroId, { endTime: Date.now(), duration, completed: false }, { silent: true }).catch(() => {});
    setCurrentPomodoro(null);
    timer.reset();
    setRecoveredSession(null);
  };

  // Global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeys = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement).tagName)) return;
      if (showNewTask || showProjectForm) return;
      switch (e.key.toLowerCase()) {
        case 's': e.preventDefault(); timer.state.isActive ? handlePause() : handleStart(); break;
        case 'r': e.preventDefault(); timer.reset(); soundManager.stopTicking(); break;
        case '1': timer.setPhase('work' as any); break;
        case '2': timer.setPhase('shortBreak' as any); break;
        case '3': timer.setPhase('longBreak' as any); break;
        case 'n': e.preventDefault(); setShowNewTask(true); break;
        case 'escape': setShowNewTask(false); setShowProjectForm(false); break;
      }
    };
    window.addEventListener('keydown', handleGlobalKeys);
    return () => window.removeEventListener('keydown', handleGlobalKeys);
  }, [timer.state.isActive, showNewTask, showProjectForm]);

  const time = formatTime(timer.state.timeRemaining);
  const progress = timer.state.timeRemaining / (
    timer.state.phase === 'work' ? settings.timer.pomodoro * 60
    : timer.state.phase === 'shortBreak' ? settings.timer.shortBreak * 60
    : settings.timer.longBreak * 60
  );

  return (
    <div className="min-h-screen">
      <PomodoroTimer selectedTask={selectedTask} />

      {/* Session Recovery Banner */}
      {recoveredSession !== null && (
        <div className="max-w-5xl mx-auto px-4 pt-4">
          <SessionRecoveryBanner
            elapsedMinutes={recoveredSession.elapsedMinutes}
            onResume={handleResumeRecovery}
            onDiscard={handleDiscardRecovery}
          />
        </div>
      )}

      {/* Hero Timer Section */}
      <div className={cn(
        "relative transition-colors duration-700",
        `bg-gradient-to-b ${currentPhase.gradient}`
      )}>
        <div className="relative max-w-5xl mx-auto px-4 pt-4 pb-6 sm:pt-8 sm:pb-10">
          {/* Pomodoro count */}
          <div className="text-center mb-2 sm:mb-4">
            <span className="text-xs sm:text-sm text-muted-foreground font-medium tracking-wide uppercase">
              {timer.state.completedPomodoros > 0
                ? `${timer.state.completedPomodoros} completed · next long break in ${settings.timer.longBreakInterval - (timer.state.completedPomodoros % settings.timer.longBreakInterval)}`
                : 'Ready to focus'
              }
            </span>
          </div>

          {/* Phase Selector */}
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="inline-flex rounded-full p-1 bg-muted/50 backdrop-blur-sm border border-border/30">
              {(['work', 'shortBreak', 'longBreak'] as const).map((phase) => {
                const config = phaseConfig[phase];
                const isActive = timer.state.phase === phase;
                return (
                  <button
                    key={phase}
                    onClick={() => timer.setPhase(phase as any)}
                    className={cn(
                      "relative px-4 sm:px-6 py-2 rounded-full text-sm font-medium transition-all duration-300",
                      isActive
                        ? "text-white shadow-md"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {isActive && (
                      <motion.div
                        layoutId="phase-bg"
                        className={cn("absolute inset-0 rounded-full", {
                          'bg-blue-500': phase === 'work',
                          'bg-emerald-500': phase === 'shortBreak',
                          'bg-violet-500': phase === 'longBreak',
                        })}
                        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                      />
                    )}
                    <span className="relative z-10">{config.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Timer Display */}
          <div className="text-center">
            <div
              className={cn("font-mono font-bold tracking-tight select-none", getTimerColor())}
              style={{ fontSize: 'clamp(3.5rem, 12vw, 9rem)', lineHeight: 1 }}
            >
              <span>{time.minutes}</span>
              <span className="mx-1">:</span>
              <span>{time.seconds}</span>
            </div>
          </div>

          {/* Task selector — compact */}
          {timer.state.phase === 'work' && (
            <div className="max-w-sm mx-auto mt-4 sm:mt-6">
              <TaskSelector tasks={tasks} selectedTask={selectedTask} onChange={handleTaskSelection} />
            </div>
          )}

          {/* Start/Pause Button */}
          <div className="flex justify-center mt-4 sm:mt-6">
            <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
              <Button
                className={cn(
                  "px-10 py-4 sm:px-14 sm:py-6 text-base sm:text-lg font-semibold rounded-full text-white shadow-lg transition-all",
                  getButtonClasses()
                )}
                onClick={timer.state.isActive ? handlePause : handleStart}
              >
                {timer.state.isActive ? (
                  <><PauseIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2" /> Pause</>
                ) : (
                  <><PlayIcon className="w-5 h-5 sm:w-6 sm:h-6 mr-2" /> Start</>
                )}
              </Button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Content Below — Music, Projects & Tasks */}
      <div className="max-w-5xl mx-auto px-4 py-6 sm:py-10">
        <div className="space-y-4 sm:space-y-6">
          {!isPlusOrHigher ? (
            <UpgradePrompt
              feature="Projects, Tasks & Music"
              description="Upgrade to Plus to organize your work with projects, tasks, and ambient sounds."
            />
          ) : (
          <>
          {/* Music Player */}
          {settings.navigation.showMusicPlayer && <MusicPlayer />}

          {/* Projects Card */}
          <div className="bg-card/60 backdrop-blur-sm rounded-2xl border border-border/50 shadow-sm hover:shadow-md transition-shadow">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setIsProjectListOpen(!isProjectListOpen)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsProjectListOpen(!isProjectListOpen); } }}
              className="w-full flex items-center justify-between p-4 sm:p-5 cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-blue-500/10">
                  <FolderIcon className="w-5 h-5 text-blue-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-foreground">Projects</h3>
                  <p className="text-xs text-muted-foreground">{projects.length} projects</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); setShowProjectForm(true); }}
                  className="h-8 w-8 p-0 hover:bg-primary/10"
                >
                  <PlusIcon className="w-4 h-4" />
                </Button>
                {isProjectListOpen ? <ChevronUpIcon className="w-4 h-4 text-muted-foreground" /> : <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />}
              </div>
            </div>
            <AnimatePresence>
              {isProjectListOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 sm:px-5 pb-4 sm:pb-5">
                    <ProjectList />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Tasks Card */}
          <div className="bg-card/60 backdrop-blur-sm rounded-2xl border border-border/50 shadow-sm hover:shadow-md transition-shadow">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setIsTaskListOpen(!isTaskListOpen)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsTaskListOpen(!isTaskListOpen); } }}
              className="w-full flex items-center justify-between p-4 sm:p-5 cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-emerald-500/10">
                  <ListBulletIcon className="w-5 h-5 text-emerald-500" />
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-foreground">Tasks</h3>
                  <p className="text-xs text-muted-foreground">{tasks.filter(t => !t.completed).length} active</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); setShowNewTask(true); }}
                  className="h-8 w-8 p-0 hover:bg-primary/10"
                >
                  <PlusIcon className="w-4 h-4" />
                </Button>
                {isTaskListOpen ? <ChevronUpIcon className="w-4 h-4 text-muted-foreground" /> : <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />}
              </div>
            </div>
            <AnimatePresence>
              {isTaskListOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-4 sm:px-5 pb-4 sm:pb-5">
                    <TaskList />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          </>
          )}
        </div>

        {/* Keyboard hint */}
        <div className="text-center mt-6 sm:mt-8">
          <p className="text-xs text-muted-foreground/50">
            Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">S</kbd> to start · <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">N</kbd> new task · <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">1</kbd><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono ml-0.5">2</kbd><kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono ml-0.5">3</kbd> switch phase
          </p>
        </div>
      </div>

      {/* Modals */}
      {showProjectForm && (
        <ProjectForm isOpen={showProjectForm} onClose={() => setShowProjectForm(false)} />
      )}

      <AnimatePresence>
        {showNewTask && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-[100]"
            onClick={() => setShowNewTask(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card rounded-2xl border shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-4">Create New Task</h3>
              <TaskForm onSubmit={handleCreateTask} onCancel={() => setShowNewTask(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
